import { useState } from 'react';
import { CourseForumThreadListItem } from '@/features/course-forum/CourseForumThreadListItem';
import { type ForumThreadSummary } from '@/features/course-forum/types';

const PAGE_SIZE = 10;

interface CourseForumThreadBoardProps {
  title: string;
  lead?: string;
  threads: ForumThreadSummary[];
  courseId: string;
  staffView?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  sortOrder: 'desc' | 'asc';
  onToggleSort: () => void;
  onNewThread: () => void;
  newThreadLabel: string;
  emptyMessage: string;
}

export function CourseForumThreadBoard({
  title,
  lead,
  threads,
  courseId,
  staffView = false,
  search,
  onSearchChange,
  sortOrder,
  onToggleSort,
  onNewThread,
  newThreadLabel,
  emptyMessage,
}: CourseForumThreadBoardProps) {
  const [page, setPage] = useState(1);
  const [filterKey, setFilterKey] = useState(`${search}|${sortOrder}`);
  const nextFilterKey = `${search}|${sortOrder}`;
  if (filterKey !== nextFilterKey) {
    setFilterKey(nextFilterKey);
    setPage(1);
  }

  const lastPage = Math.max(1, Math.ceil(threads.length / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  const rangeStart = threads.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, threads.length);
  const pageThreads = threads.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="lms-course-forum-card">
      <div className="lms-course-forum-card__head">
        <div className="lms-course-forum-card__heading">
          <h2 className="lms-course-forum-card__title">{title}</h2>
          {lead ? <p className="lms-course-forum-card__lead">{lead}</p> : null}
        </div>
        <div className="lms-course-forum-card__tools">
          <div className="lms-course-forum-card__search">
            <span className="material-symbols-outlined lms-course-forum-card__search-icon" aria-hidden="true">
              search
            </span>
            <input
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Buscar en el foro..."
              className="lms-course-forum-card__search-input"
              aria-label="Buscar en el foro"
            />
          </div>
          <button type="button" className="lms-course-forum-card__filter" onClick={onToggleSort}>
            <span className="material-symbols-outlined" aria-hidden="true">
              sort
            </span>
            {sortOrder === 'desc' ? 'Más recientes' : 'Más antiguos'}
          </button>
          <button type="button" className="lms-course-forum-btn lms-course-forum-btn--primary" onClick={onNewThread}>
            <span className="material-symbols-outlined" aria-hidden="true">
              add
            </span>
            {newThreadLabel}
          </button>
        </div>
      </div>

      <div className="lms-course-forum-card__list">
        {pageThreads.length === 0 ? (
          <div className="lms-course-forum-empty lms-course-forum-empty--compact">{emptyMessage}</div>
        ) : (
          pageThreads.map((thread) => (
            <CourseForumThreadListItem key={thread.id} courseId={courseId} thread={thread} staffView={staffView} />
          ))
        )}
      </div>

      {threads.length > 0 ? (
        <div className="lms-course-forum-card__footer">
          <span className="lms-course-forum-card__meta">
            Mostrando <strong>{rangeStart}</strong> a <strong>{rangeEnd}</strong> de <strong>{threads.length}</strong>{' '}
            resultados
          </span>
          <div className="lms-course-forum-card__pager">
            <button
              type="button"
              className="lms-course-forum-card__pager-btn"
              aria-label="Página anterior"
              disabled={currentPage <= 1}
              onClick={() => setPage(Math.max(1, currentPage - 1))}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                chevron_left
              </span>
            </button>
            <span className="lms-course-forum-card__pager-current">{currentPage}</span>
            <button
              type="button"
              className="lms-course-forum-card__pager-btn"
              aria-label="Página siguiente"
              disabled={currentPage >= lastPage}
              onClick={() => setPage(Math.min(lastPage, currentPage + 1))}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                chevron_right
              </span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
